import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import './ExcelUpload.css';

const ExcelUpload = ({ user }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [template, setTemplate] = useState(null);
  const [importHistory, setImportHistory] = useState([]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type.includes('excel') || selectedFile.type.includes('spreadsheet') || 
          selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
        setError('');
      } else {
        setError('Please select an Excel file (.xlsx or .xls)');
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('excel_file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_BASE_URL+'/excel_upload.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setResult(data.data);
        setFile(null);
        
        // Add to import history
        const newImport = {
          id: importHistory.length + 1,
          filename: file.name,
          date: new Date().toLocaleString(),
          imported: data.data.imported_rows,
          rejected: data.data.rejected_rows,
          status: 'success'
        };
        setImportHistory([newImport, ...importHistory]);
        
        // Reset file input
        document.getElementById('excel-file').value = '';
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Upload failed. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create template workbook
    const wb = XLSX.utils.book_new();
    
    // Vehicles sheet
    const vehiclesData = [
      ['VehicleNumber', 'Type', 'DriverName', 'DriverPhone', 'Vendor', 'Plat Name'],
      ['MH12AB1234', 'Truck', 'Rajesh Kumar', '9876543210', 'ABC Transport', 'HPCL PURNIA'],
      ['DL01CD5678', 'Trailer', 'Amit Sharma', '8765432109', 'XYZ Logistics', 'HPCL PANAGARH'],
      ['KA05EF9012', 'Van', 'Suresh Patel', '7654321098', 'Quick Delivery', 'HPCL ARAH']
    ];
    const vehiclesWs = XLSX.utils.aoa_to_sheet(vehiclesData);
    
    // Add column widths
    const vehiclesCols = [
      {wch: 15}, // VehicleNumber
      {wch: 10}, // Type
      {wch: 20}, // DriverName
      {wch: 15}, // DriverPhone
      {wch: 20}, // Vendor
      {wch: 20}  // StartingPlace
    ];
    vehiclesWs['!cols'] = vehiclesCols;
    
    XLSX.utils.book_append_sheet(wb, vehiclesWs, 'Vehicles');
    
    // TripsToday sheet
    const tripsData = [
      ['VehicleNumber', 'Action', 'Origin', 'Destination', 'Timestamp', 'Notes'],
      ['MH12AB1234', 'BOARD', 'Mumbai Depot', 'Delhi Warehouse', '2024-01-15 08:00:00', 'Loaded with electronics'],
      ['DL01CD5678', 'UNBOARD', 'Delhi Warehouse', 'Delhi Terminal', '2024-01-15 18:00:00', 'Unloaded successfully'],
      ['KA05EF9012', 'BOARD', 'Bangalore Hub', 'Hyderabad Center', '2024-01-15 09:30:00', 'Medical supplies delivery']
    ];
    const tripsWs = XLSX.utils.aoa_to_sheet(tripsData);
    
    // Add column widths for trips sheet
    const tripsCols = [
      {wch: 15}, // VehicleNumber
      {wch: 10}, // Action
      {wch: 20}, // Origin
      {wch: 20}, // Destination
      {wch: 20}, // Timestamp
      {wch: 30}  // Notes
    ];
    tripsWs['!cols'] = tripsCols;
    
    XLSX.utils.book_append_sheet(wb, tripsWs, 'TripsToday');
    
    // Generate and download
    XLSX.writeFile(wb, 'Vehicle_Tracking_Template.xlsx');
  };

  const previewFile = () => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      
      const result = {};
      workbook.SheetNames.forEach(sheetName => {
        const sheet = workbook.Sheets[sheetName];
        const sheetData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        result[sheetName] = sheetData;
      });
      
      setTemplate(result);
    };
    reader.readAsArrayBuffer(file);
  };

  const clearPreview = () => {
    setTemplate(null);
    setFile(null);
    document.getElementById('excel-file').value = '';
  };

  return (
    <div className="excel-upload-container">
      <div className="upload-header">
        <h1>Excel Upload</h1>
        <p className="page-subtitle">Upload daily vehicle and trip data from Excel sheets</p>
      </div>

      <div className="upload-card">
        <div className="upload-instructions">
          <h3>📋 How to Upload:</h3>
          <ol>
            <li>Download the template using the button below</li>
            <li>Fill in the data in two sheets: "Vehicles" and "TripsToday"</li>
            <li>Save the Excel file</li>
            <li>Upload using the form on this page</li>
            <li>System will validate and import the data automatically</li>
          </ol>
          <div className="note-box">
            <strong>Note:</strong> VehicleNumber must be unique. Duplicates will be rejected.
          </div>
        </div>

        <div className="upload-zone">
          <div className="zone-content">
            <div className="upload-icon">📊</div>
            <h3>Drop Excel File Here</h3>
            <p>or click to select</p>
            <p className="file-types">Supported formats: .xlsx, .xls</p>
            
            <input
              type="file"
              id="excel-file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="file-input"
            />
            <label htmlFor="excel-file" className="btn-browse">
              Browse Files
            </label>
            
            {file && (
              <div className="file-info">
                <div className="file-details">
                  <span className="file-icon">📄</span>
                  <div className="file-detail">
                    <strong>Selected:</strong> {file.name}
                  </div>
                  <div className="file-detail">
                    <strong>Size:</strong> {(file.size / 1024).toFixed(2)} KB
                  </div>
                </div>
                <div className="file-actions">
                  <button className="btn-preview" onClick={previewFile}>
                    <span>👁️</span> Preview
                  </button>
                  <button className="btn-clear" onClick={clearPreview}>
                    <span>🗑️</span> Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="upload-actions">
          <button 
            className="btn-secondary"
            onClick={downloadTemplate}
            disabled={uploading}
          >
            <span>📥</span> Download Template
          </button>
          
          <button 
            className="btn-primary"
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? (
              <>
                <span className="spinner"></span>
                Uploading...
              </>
            ) : (
              <>
                <span>📤</span> Upload & Process
              </>
            )}
          </button>
        </div>

        {error && (
          <div className="alert alert-danger">
            <span>❌</span> {error}
          </div>
        )}
      </div>

      {result && (
        <div className="result-card">
          <div className="result-header">
            <h3>Upload Results</h3>
            <span className="result-status success">Success</span>
          </div>
          <div className="result-stats">
            <div className="stat success">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h4>Imported</h4>
                <p>{result.imported_rows}</p>
              </div>
            </div>
            <div className="stat rejected">
              <div className="stat-icon">❌</div>
              <div className="stat-content">
                <h4>Rejected</h4>
                <p>{result.rejected_rows}</p>
              </div>
            </div>
            <div className="stat total">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h4>Total</h4>
                <p>{result.total_rows}</p>
              </div>
            </div>
          </div>
          
          {result.rejected_details && result.rejected_details.length > 0 && (
            <div className="rejected-details">
              <h4>Rejection Details:</h4>
              <div className="rejected-list">
                {result.rejected_details.map((detail, index) => (
                  <div key={index} className="rejected-item">
                    <span className="reject-icon">⚠️</span>
                    <span className="reject-text">{detail}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {template && (
        <div className="preview-card">
          <div className="preview-header">
            <h3>File Preview</h3>
            <button className="btn-close" onClick={() => setTemplate(null)}>×</button>
          </div>
          <div className="preview-content">
            {Object.keys(template).map(sheetName => (
              <div key={sheetName} className="sheet-preview">
                <h4>
                  <span className="sheet-icon">📋</span>
                  {sheetName} Sheet
                  <span className="sheet-count">
                    ({template[sheetName].length} rows)
                  </span>
                </h4>
                <div className="preview-table-container">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        {Object.keys(template[sheetName][0] || {}).map(key => (
                          <th key={key}>{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {template[sheetName].slice(0, 5).map((row, index) => (
                        <tr key={index}>
                          {Object.values(row).map((value, idx) => (
                            <td key={idx}>{value || <span className="empty-cell">—</span>}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {template[sheetName].length > 5 && (
                    <div className="preview-more">
                      ... and {template[sheetName].length - 5} more rows
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {importHistory.length > 0 && (
        <div className="history-card">
          <h3>Recent Imports</h3>
          <div className="history-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Filename</th>
                  <th>Imported</th>
                  <th>Rejected</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {importHistory.slice(0, 5).map(importItem => (
                  <tr key={importItem.id}>
                    <td>{importItem.date}</td>
                    <td className="filename">{importItem.filename}</td>
                    <td>
                      <span className="imported-count">{importItem.imported}</span>
                    </td>
                    <td>
                      <span className={`rejected-count ${importItem.rejected > 0 ? 'has-rejects' : ''}`}>
                        {importItem.rejected}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${importItem.status}`}>
                        {importItem.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelUpload;